import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useQuery} from '@github-ui/react-query'
import {useDebounce} from '@github-ui/use-debounce'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useCallback, useState} from 'react'

interface Repository {
  id: number
  name: string
  owner: string
  nameWithOwner: string
  visibility: string
}

interface RepositoriesResponse {
  repositories: Repository[]
}

export interface UseRepositorySuggestionsOptions {
  owner: string
  enabled: boolean
  currentRepositoryName: string
  currentRepositoryVisibility: 'public' | 'private' | 'internal'
}

export interface UseRepositorySuggestionsResult {
  repositories: Repository[]
  isLoading: boolean
  searchQuery: string
  handleFilterChange: (value: string) => void
}

export function useRepositorySuggestions({
  owner,
  enabled,
  currentRepositoryName,
  currentRepositoryVisibility,
}: UseRepositorySuggestionsOptions): UseRepositorySuggestionsResult {
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')

  const shouldDebounce = isFeatureEnabled('global_nav_repo_picker_debounce')

  // Debounce the setter to avoid sending requests on every keystroke
  const debouncedSetQuery = useDebounce((value: string) => setDebouncedSearchQuery(value), 300, {
    leading: false,
    trailing: true,
  })

  const handleFilterChange = useCallback(
    (value: string) => {
      setSearchQuery(value)
      if (shouldDebounce) {
        debouncedSetQuery(value)
      } else {
        // When flag is off, update immediately (no debounce)
        setDebouncedSearchQuery(value)
      }
    },
    [debouncedSetQuery, shouldDebounce],
  )

  const {data, isLoading} = useQuery<RepositoriesResponse>({
    queryKey: ['global-nav-repo-picker', owner, debouncedSearchQuery, currentRepositoryName, shouldDebounce],
    queryFn: async ({signal}) => {
      if (!owner) {
        return {repositories: []}
      }

      const url = new URL(`/_global-navigation/repositories/${encodeURIComponent(owner)}`, window.location.origin)
      if (debouncedSearchQuery) {
        url.searchParams.set('query', debouncedSearchQuery)
      }

      // the current repository name is sent to the server so that it can be prioritized if it is included in the results
      // repo names are unique within an owner, so we'll use that since IDs are not included in the payload
      if (currentRepositoryName) {
        url.searchParams.set('currentRepository', currentRepositoryName)
      }

      // Only pass signal when feature flag is enabled to allow request cancellation
      const response = await reactFetchJSON(url.toString(), shouldDebounce ? {signal} : undefined)

      if (!response.ok) {
        return {repositories: []}
      }

      return response.json() as Promise<RepositoriesResponse>
    },
    placeholderData: previousData => previousData,
    enabled: !!owner && enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  })

  let currentRepositoryObject: Repository | null = null
  const repositories = [] as Repository[]
  for (const repo of data?.repositories ?? []) {
    if (repo.name === currentRepositoryName) {
      currentRepositoryObject = repo
    } else {
      repositories.push(repo)
    }
  }

  if (currentRepositoryObject) {
    // Ensure the current repository appears at the top of the list
    repositories.unshift(currentRepositoryObject)
  } else if (currentRepositoryName.toLowerCase().includes(searchQuery.toLowerCase())) {
    // If the current repository is not in the results but matches the search query, add placeholder to the top
    // Use the immediate searchQuery (not debounced) for instant UI feedback
    repositories.unshift({
      id: -1,
      name: currentRepositoryName,
      owner,
      nameWithOwner: `${owner}/${currentRepositoryName}`,
      visibility: currentRepositoryVisibility,
    })
  }

  return {
    repositories,
    isLoading,
    searchQuery,
    handleFilterChange,
  }
}

export type {Repository}
